import { getApi, postApi } from 'apis';
import { Button, Select } from 'antd';
import { FC, useCallback, useEffect, useState } from 'react';
import { toastText } from 'utils/utils';
import styles from './index.module.scss';
import { EditMappingDrawerBodyProps } from './types';

const EditMappingDrawerBody: FC<EditMappingDrawerBodyProps> = (props) => {
	const { selectedCategory, closeDrawerByAnimation, fetchTableData } = props;
	const [doseFormOptions, setDoseFormOptions] = useState<{ label: string; value: string }[]>([]);
	const [selectedDoseFormIds, setSelectedDoseFormIds] = useState<string[]>([]);
	const [loadingMappings, setLoadingMappings] = useState(false);
	const [saving, setSaving] = useState(false);

	const fetchDoseForms = useCallback(async () => {
		try {
			const res = await getApi('/dose-form/get-all', { limit: 1000 });
			const data = res?.data?.data || [];
			setDoseFormOptions(data.map((d: { id: string; name: string }) => ({ label: d.name, value: d.id })));
		} catch (error: any) {
			toastText(error?.response?.data?.error?.errorDescription ?? 'Failed to load dose forms', 'error');
		}
	}, []);

	const fetchMappings = useCallback(async (categoryId: string) => {
		setLoadingMappings(true);
		try {
			const res = await getApi(`/category-dose-form-mapping/get-by-category/${categoryId}`);
			const mappings = res?.data?.data || [];
			setSelectedDoseFormIds(mappings.map((m: { id: string }) => m.id));
		} catch (error: any) {
			toastText(
				error?.response?.data?.error?.errorDescription ?? 'Failed to load mappings',
				'error'
			);
			setSelectedDoseFormIds([]);
		} finally {
			setLoadingMappings(false);
		}
	}, []);

	useEffect(() => {
		fetchDoseForms();
	}, [fetchDoseForms]);

	useEffect(() => {
		if (selectedCategory?.id) {
			fetchMappings(selectedCategory.id);
		} else {
			setSelectedDoseFormIds([]);
		}
	}, [selectedCategory?.id, fetchMappings]);

	const handleSave = async () => {
		if (!selectedCategory?.id) return;
		setSaving(true);
		try {
			const res = await postApi('/category-dose-form-mapping/upsert', {
				categoryId: selectedCategory.id,
				doseFormIds: selectedDoseFormIds,
			});
			toastText(res?.data?.message ?? 'Mapping saved successfully', 'success');
			fetchTableData();
			closeDrawerByAnimation();
		} catch (error: any) {
			toastText(
				error?.response?.data?.message ??
					error?.response?.data?.error?.errorDescription ??
					'Failed to save mapping',
				'error'
			);
		} finally {
			setSaving(false);
		}
	};

	if (!selectedCategory) return null;

	return (
		<div className={styles['side-drawer-body']}>
			<div className={styles['side-drawer-form']}>
				<div className={styles['side-drawer-form__inputs']}>
					<div className={styles['side-drawer-form__role']}>
						<div className={styles['side-drawer-form__role--label']}>Category</div>
						<div className={styles['side-drawer-form__role--select']}>
							{selectedCategory.categoryName}
						</div>
					</div>
					<div className={styles['side-drawer-form__role']}>
						<div className={styles['side-drawer-form__role--label']}>Dose Forms</div>
						<Select
							mode="multiple"
							placeholder="Select dose forms"
							size="large"
							className={styles['side-drawer-form__role--select']}
							style={{ width: '100%' }}
							value={selectedDoseFormIds}
							onChange={setSelectedDoseFormIds}
							options={doseFormOptions}
							loading={loadingMappings}
							showSearch
							optionFilterProp="label"
						/>
					</div>
				</div>
				<div className={styles['side-drawer-form__buttons']}>
					<Button
						className={styles['side-drawer-form__buttons--edit']}
						onClick={handleSave}
						loading={saving}
					>
						Save
					</Button>
					<Button
						className={styles['side-drawer-form__buttons--cancel']}
						onClick={closeDrawerByAnimation}
					>
						Cancel
					</Button>
				</div>
			</div>
		</div>
	);
};

export default EditMappingDrawerBody;
